import {
  MEETING_DURATION_MAX_MINUTES,
  MEETING_DURATION_MIN_MINUTES,
  MEETING_ERROR_CODES,
  MEETING_ERROR_MESSAGES,
  MEETING_ID_LENGTH,
  MEETING_PASSWORD_MAX_LENGTH,
  MEETING_PASSWORD_MIN_LENGTH,
  MEETING_SEATS_MAX_DEFAULT,
  MEETING_SEATS_MIN,
  MEETING_TITLE_MAX_LENGTH,
  MEETING_TITLE_MIN_LENGTH,
  MEETING_TITLE_REGEX,
  PARTICIPANT_NAME_MIN_LENGTH,
  PARTICIPANT_NAME_REGEX,
} from '../constants/meetings.constants'
import type { ValidationErrors } from '../services/meetings-error'
import type { RecurringConfig } from '../models/meeting.model'
import { recurrenceEndDateProblem } from './recurrence'
import { zonedTimeToUtcMs } from '@/shared/utils/timezone-convert'

const M = MEETING_ERROR_MESSAGES

export interface TitleCheckOptions {
  field?: string
  required?: boolean
}

export function validateTitle(
  value: string,
  errors: ValidationErrors,
  opts: TitleCheckOptions = {},
): void {
  const field = opts.field ?? 'title'
  const trimmed = value.trim()
  if (!trimmed) {
    if (opts.required !== false) errors[field] = M[MEETING_ERROR_CODES.TITLE_REQUIRED]
    return
  }
  if (trimmed.length < MEETING_TITLE_MIN_LENGTH) {
    errors[field] = M[MEETING_ERROR_CODES.TITLE_TOO_SHORT]
    return
  }
  if (trimmed.length > MEETING_TITLE_MAX_LENGTH) {
    errors[field] = `Meeting name cannot exceed ${MEETING_TITLE_MAX_LENGTH} characters.`
    return
  }
  if (!MEETING_TITLE_REGEX.test(trimmed)) {
    errors[field] = M[MEETING_ERROR_CODES.TITLE_INVALID_CHARS]
  }
}

export function validateParticipantName(
  value: string,
  errors: ValidationErrors,
  field = 'name',
): void {
  const trimmed = value.trim()
  if (!trimmed) {
    errors[field] = M[MEETING_ERROR_CODES.NAME_REQUIRED]
    return
  }
  if (trimmed.length < PARTICIPANT_NAME_MIN_LENGTH) {
    errors[field] = M[MEETING_ERROR_CODES.NAME_TOO_SHORT]
    return
  }
  if (!PARTICIPANT_NAME_REGEX.test(trimmed)) {
    errors[field] = M[MEETING_ERROR_CODES.NAME_INVALID_CHARS]
  }
}

/**
 * A meeting password the user typed themselves.
 *
 * Only the LENGTH is bounded. The alphabet is deliberately not restricted — a
 * password the host invents is theirs to choose, and the stored form is a hash,
 * so nothing downstream cares what characters it holds.
 *
 * Length is checked on the trimmed value but the untrimmed string is what gets
 * stored, matching how the field has always behaved; the input's own maxLength
 * stops most of this being reachable from the schedule form at all.
 */
export function validatePassword(
  value: string,
  errors: ValidationErrors,
  field = 'password',
): void {
  const trimmed = value.trim()
  if (!trimmed) {
    errors[field] = 'Password cannot be empty.'
    return
  }
  if (trimmed.length < MEETING_PASSWORD_MIN_LENGTH) {
    errors[field] = `Password must be at least ${MEETING_PASSWORD_MIN_LENGTH} characters.`
    return
  }
  if (trimmed.length > MEETING_PASSWORD_MAX_LENGTH) {
    errors[field] = `Password cannot exceed ${MEETING_PASSWORD_MAX_LENGTH} characters.`
  }
}

export function validateMeetingIdFormat(
  value: string,
  errors: ValidationErrors,
  field = 'meetingId',
): void {
  if (!value) {
    errors[field] = M[MEETING_ERROR_CODES.ID_REQUIRED]
    return
  }
  const isAllDigits = /^\d+$/.test(value)
  if (!isAllDigits || value.length !== MEETING_ID_LENGTH) {
    errors[field] = M[MEETING_ERROR_CODES.ID_INVALID_FORMAT]
  }
}

export function validateDuration(
  durationMinutes: number,
  errors: ValidationErrors,
  field = 'duration',
): void {
  if (!Number.isFinite(durationMinutes) || durationMinutes < MEETING_DURATION_MIN_MINUTES) {
    errors[field] = M[MEETING_ERROR_CODES.DURATION_INVALID]
    return
  }
  if (durationMinutes > MEETING_DURATION_MAX_MINUTES) {
    errors[field] = M[MEETING_ERROR_CODES.DURATION_TOO_LONG]
  }
}

export function validateSeats(
  seats: number,
  errors: ValidationErrors,
  maxSeats = MEETING_SEATS_MAX_DEFAULT,
  field = 'seats',
): void {
  if (!Number.isFinite(seats) || seats < MEETING_SEATS_MIN) {
    errors[field] = `Minimum ${MEETING_SEATS_MIN} seats required.`
    return
  }
  if (seats > maxSeats) {
    errors[field] = `Maximum ${maxSeats} seats for this license.`
  }
}

export interface StartTimeInput {
  date?: Date
  time?: string // "HH:mm"
  /** Allow start times that are within the early-join window even if slightly past now. */
  allowEarlyWindow?: boolean
  /**
   * The zone the picked date/time is wall-clock in. Omitted, this keeps the
   * old behaviour (the browser's own zone) so callers that have not been
   * updated yet are unaffected. Given, the calendar date and "HH:mm" the user
   * picked are read as wall-clock in THIS zone rather than in whatever zone
   * the browser happens to be running in, which is the whole point of
   * letting someone schedule a meeting in a zone other than their own.
   */
  timeZone?: string
}

export function resolveStartMs({ date, time, timeZone }: StartTimeInput): number | null {
  if (!date || !time) return null
  const [h, m] = time.split(':').map((n) => Number(n))
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null
  if (timeZone) {
    return zonedTimeToUtcMs(
      date.getFullYear(),
      date.getMonth() + 1,
      date.getDate(),
      h,
      m,
      timeZone,
    )
  }
  const d = new Date(date)
  d.setHours(h, m, 0, 0)
  return d.getTime()
}

export function validateStartTime(
  input: StartTimeInput,
  errors: ValidationErrors,
  dateField = 'date',
  timeField = 'time',
): void {
  if (!input.date) {
    errors[dateField] = M[MEETING_ERROR_CODES.DATE_REQUIRED]
    return
  }
  if (!input.time) {
    errors[timeField] = M[MEETING_ERROR_CODES.TIME_REQUIRED]
    return
  }
  const startMs = resolveStartMs(input)
  if (startMs == null) {
    errors[timeField] = M[MEETING_ERROR_CODES.TIME_REQUIRED]
    return
  }
  if (startMs <= Date.now()) {
    errors[timeField] = M[MEETING_ERROR_CODES.START_IN_PAST]
  }
}

export function validateRecurring(
  cfg: RecurringConfig | undefined,
  enabled: boolean,
  errors: ValidationErrors,
  startDate?: Date,
): void {
  if (!enabled || !cfg) return
  if (cfg.frequency === 'weekly' && (!cfg.weekDays || cfg.weekDays.length === 0)) {
    errors.recurringWeekDays = M[MEETING_ERROR_CODES.RECURRING_WEEKDAYS_REQUIRED]
  }
  if (!Number.isFinite(cfg.occurrences) || cfg.occurrences < 1) {
    errors.recurringOccurrences =
      M[MEETING_ERROR_CODES.RECURRING_OCCURRENCES_INVALID]
  }
  if (recurrenceEndDateProblem(cfg, startDate)) {
    errors.recurringEndDate = M[MEETING_ERROR_CODES.RECURRING_END_DATE_INVALID]
  }
}

/** Round "now" to the next half-hour and return an "HH:mm" string. */
export function getDefaultFutureTime(): string {
  const d = new Date()
  d.setMinutes(d.getMinutes() + 15)
  const mins = d.getMinutes()
  const rounded = mins < 30 ? 30 : 0
  if (rounded === 0) d.setHours(d.getHours() + 1)
  d.setMinutes(rounded, 0, 0)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/**
 * Generate a numeric 10-digit meeting ID (no leading zero so it always
 * formats as 10 digits when serialised to a string).
 */
export function generateMeetingId(): string {
  return String(Math.floor(1_000_000_000 + Math.random() * 9_000_000_000))
}
